import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BatchItem, Card, ItemKind, Row } from '../types';
import { FLAG_LABELS, LOCALE_META, describe } from '../lib/locales';
import { missingTokens, paintHtml, repaint, tokensOf } from '../lib/tokens';
import { rateCard } from '../lib/api';
import NoteThread from './NoteThread';
import VibeSlider from './VibeSlider';

/** Read-only text with its placeholders marked, for the English side. */
function Painted({ text }: { text: string }) {
  const marks = new Set(tokensOf(text));
  return (
    <>
      {String(text).split(/(\{[A-Z_]+\})/g).map((piece, i) =>
        marks.has(piece)
          ? <span key={i} className="tok">{piece}</span>
          : <span key={i}>{piece}</span>)}
    </>
  );
}

/** Both halves of a deck prompt are fields of equal weight. A translator moves
 *  wording between them constantly, so the instruction is labelled rather than
 *  shrunk — it used to render at 11.5px grey and read as a footnote. */
const partLabel = (r: Row) =>
  r.kind !== 'deck' ? '' : r.field === 'instruction' ? 'Instruction' : 'Question';

function caretToEnd(el: HTMLElement) {
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

/**
 * What the card is for right now.
 *
 * `full` is the queue: wording and vibe together, the vibe mandatory. Focus
 * mode narrows it — `translate` drops the slider so a UI string or a quick
 * wording pass is not held up by a question it cannot answer, and `vibe` makes
 * the text read-only and turns a single keypress into the whole decision.
 */
export type CardMode = 'full' | 'translate' | 'vibe';

export interface PromptCardProps {
  card: Card;
  mode?: CardMode;
  decided?: BatchItem[];
  meId: string;
  onDecide: (card: Card, kind: ItemKind, payload: { values?: Map<string, string>; note?: string }) => void;
  onUndo: (card: Card) => void;
  onNotesChanged: () => void;
  /** Lets the queue put the caret in this card. Null unregisters, which is how
   *  a saved card drops out of the walk. */
  onRegisterFocus?: (key: string, focus: (() => void) | null) => void;
  /** Move to the next card still waiting for a decision. */
  onAdvance?: (key: string) => void;
}

export default function PromptCard({
  card, mode = 'full', decided, meId, onDecide, onUndo, onNotesChanged, onRegisterFocus, onAdvance,
}: PromptCardProps) {
  const vibeOnly = mode === 'vibe';
  const askVibe = mode !== 'translate';
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const boxes = useRef(new Map<string, HTMLDivElement | null>());
  const slider = useRef<HTMLInputElement | null>(null);
  const composing = useRef(false);

  // Only a deck prompt can be judged as a card. "Sound Effects" is a label,
  // not a question, and scoring it 0 to 9 would mean nothing.
  const isDeck = card.kind === 'deck' && Boolean(card.prompt_id);
  const [vibe, setVibe] = useState<number | null>(card.vibe?.mine ?? null);
  const [vibeError, setVibeError] = useState<string | null>(null);
  useEffect(() => { setVibe(card.vibe?.mine ?? null); }, [card.vibe?.mine]);

  // What is in the boxes right now. Held in state so the missing-token warning
  // under a field can update as it is typed; the boxes themselves are never
  // rendered by React, or it would fight the highlighting for control of them.
  const [texts, setTexts] = useState<Map<string, string>>(
    () => new Map(card.parts.map((p) => [p.unit_id, p.value ?? ''])));

  const valueKey = card.parts.map((p) => `${p.unit_id} ${p.value ?? ''}`).join(' | ');

  const fill = useCallback(() => {
    for (const p of card.parts) {
      const el = boxes.current.get(p.unit_id);
      if (el) el.innerHTML = paintHtml(p.value ?? '', p.source_text);
    }
    setTexts(new Map(card.parts.map((p) => [p.unit_id, p.value ?? ''])));
    // card.parts is rebuilt on every fetch; the joined values are what actually
    // decide whether the boxes need refilling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  useEffect(() => { fill(); }, [fill]);

  const notes = card.notes ?? [];
  const openQ = notes.filter((n) => n.kind === 'question' && !n.answered_at).length;
  // An unanswered question opens the thread by itself. Hiding it behind a
  // click is how the same thing gets asked twice.
  const [showNotes, setShowNotes] = useState(openQ > 0);

  const isDone = Boolean(decided?.length);
  const firstUnit = card.parts[0]?.unit_id;

  const focusFirst = useCallback(() => {
    // Rating only: the slider is the one control, so the caret goes there.
    const el = vibeOnly ? slider.current : firstUnit ? boxes.current.get(firstUnit) : null;
    if (!el) return;
    el.focus();
    if (!vibeOnly) caretToEnd(el as HTMLElement);
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [firstUnit, vibeOnly]);

  // A decided card has no boxes left to focus, so it takes itself out of the
  // walk and the caret skips to the next one still open.
  useEffect(() => {
    if (!onRegisterFocus) return;
    if (isDone) { onRegisterFocus(card.key, null); return; }
    onRegisterFocus(card.key, focusFirst);
    return () => onRegisterFocus(card.key, null);
  }, [card.key, isDone, focusFirst, onRegisterFocus]);

  const flags = [...new Set(card.parts.flatMap((p) => p.flags))];
  const busy = card.inflight;
  const STAGE = {
    drafting: 'is working on this',
    submitted: 'has proposed a change, waiting on Sam',
    approved: 'change is approved and waiting to ship',
  } as const;
  const severity = flags.includes('placeholder_mismatch') || flags.includes('missing')
    ? 'crit' : flags.length ? 'warn' : 'clean';

  const dirty = useMemo(
    () => card.parts.some((p) => (texts.get(p.unit_id) ?? '') !== (p.value ?? '')),
    [card.parts, texts]);

  const onEdit = (p: Row, el: HTMLDivElement) => {
    if (!el.textContent?.trim()) el.innerHTML = '';
    else if (!composing.current) repaint(el, p.source_text);
    setTexts((cur) => new Map(cur).set(p.unit_id, el.textContent ?? ''));
  };

  const blocked = Boolean(busy) && !dirty;
  const needsVibe = isDeck && askVibe && vibe === null;

  // Rating only: the score is the decision. Nothing enters the batch — a vibe
  // is an opinion about the game, not a change to the repo.
  const rateAndGo = async (score: number) => {
    try {
      await rateCard(card.prompt_id as string, card.locale, score);
    } catch (e) {
      setVibeError(e instanceof Error ? e.message : String(e));
      return;
    }
    onAdvance?.(card.key);
  };

  // Rating only, and the slider is not focused: a digit anywhere on the page
  // still counts. The slider handles its own keys, so its events are left to it.
  useEffect(() => {
    if (!vibeOnly || isDone) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.target === slider.current || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        const n = Number(e.key);
        setVibe(n); setVibeError(null);
        void rateAndGo(n);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vibeOnly, isDone, card.key]);

  const save = async () => {
    if (vibeOnly) {
      if (vibe !== null) void rateAndGo(vibe);
      return;
    }
    if (blocked || needsVibe) return;
    if (isDeck && askVibe && vibe !== null) {
      // Written before the card is put away rather than after: a score that
      // failed to save is worth stopping for, and the reviewer is still here.
      try {
        await rateCard(card.prompt_id as string, card.locale, vibe);
      } catch (e) {
        setVibeError(e instanceof Error ? e.message : String(e));
        return;
      }
    }
    onDecide(card, dirty ? 'fix' : 'ok', { values: new Map(texts) });
    onAdvance?.(card.key);
  };

  // Enter walks the card: question to instruction, instruction to saved, saved
  // to the next prompt. Shift+Enter still breaks a line, for the rare prompt
  // that wants one.
  const onFieldKey = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    const next = card.parts[index + 1];
    if (next) {
      const el = boxes.current.get(next.unit_id);
      if (el) { el.focus(); caretToEnd(el); }
      return;
    }
    // The last field hands over to the slider, so the one mandatory answer is
    // on the way through rather than a wall at the end.
    if (isDeck && askVibe) { slider.current?.focus(); return; }
    void save();
  };

  const notesToggle = (
    <button
      className={`btn sm${openQ && !showNotes ? ' warn' : ''}`}
      disabled={!notes.length}
      title={notes.length
        ? undefined
        : 'Nothing has been asked about this prompt yet — Ask Sam starts a thread'}
      onClick={() => setShowNotes((v) => !v)}
    >
      {notes.length ? `Toggle notes (${notes.length})` : 'No notes'}
    </button>
  );

  const notesBlock = showNotes && notes.length > 0 && (
    <NoteThread
      notes={notes}
      // Both halves of a deck prompt share one thread; new notes land on the
      // half a reviewer reads first, so the conversation stays in one place.
      unitId={firstUnit ?? ''}
      locale={card.locale}
      meId={meId}
      onPosted={onNotesChanged}
    />
  );

  const langName = LOCALE_META[card.locale]?.name ?? card.locale;

  const meta = (
    <div className="rmeta">
      <span className="rid">{card.prompt_id ?? card.parts[0]?.field.slice(0, 20)}</span>
      <span className="rpath">
        {describe(card.parts[0] ?? { kind: card.kind, deck: card.deck, prompt_id: card.prompt_id, field: '' })}
      </span>
      <span className="right">
        <span className="pill p-acc">{LOCALE_META[card.locale]?.native ?? card.locale}</span>
        {!isDone && !vibeOnly && flags.map((f) => (
          <span key={f} className={`pill ${f === 'placeholder_mismatch' || f === 'missing' ? 'p-crit' : 'p-warn'}`}>
            {FLAG_LABELS[f] ?? f}
          </span>
        ))}
      </span>
    </div>
  );

  if (isDone) {
    const changed = decided!.filter((d) => d.kind === 'fix').length;
    const asked = decided!.some((d) => d.kind === 'question');
    const label = asked ? 'Question added to your batch'
      : changed ? `Saved with ${changed} change${changed === 1 ? '' : 's'}`
      : 'Confirmed correct';
    const tone = asked ? 'p-warn' : changed ? 'p-acc' : 'p-ok';
    return (
      <article className="row clean done">
        <div className="st" />
        <div className="rbody">
          {meta}
          <div className="donestrip">
            <span className={`pill ${tone}`}>✓</span>
            <span>
              <span className="msg">{label}</span>{' '}
              <span className="con">In your batch — nothing is sent until you submit it.</span>
            </span>
            {vibe !== null && <span className="pill p-mute">vibe {vibe}</span>}
            <span className="spacer" style={{ flex: 1 }} />
            {notesToggle}
            <button className="btn sm" onClick={() => onUndo(card)}>Undo</button>
          </div>
          {notesBlock}
        </div>
      </article>
    );
  }

  return (
    <article className={`row ${severity} ${dirty ? 'edited' : ''}`}>
      <div className="st" />
      <div className="rbody">
        {meta}

        {busy && !vibeOnly && (
          <div className="inflight">
            <span className="pill p-warn">In progress</span>
            <span>
              <b>{busy.by_name}</b> {STAGE[busy.stage]}.
              {busy.proposed_text ? <> Proposed: <em>“{busy.proposed_text}”</em></> : null}
              <span className="d">
                Leave this one — deciding it again would duplicate their work, and only one of
                the two can reach the repo.
              </span>
            </span>
          </div>
        )}

        {/* One grid rather than two independent columns: a row is as tall as
            the taller of its two cells, so a translation stays level with the
            English it belongs to however differently the two wrap. */}
        <div className="pair">
          <span className="pcap">English — source</span>
          <span className="pcap">{langName} — translating</span>

          {card.parts.map((p, i) => {
            const missing = missingTokens(texts.get(p.unit_id) ?? '', p.source_text);
            return (
              <Fragment key={p.unit_id}>
                {partLabel(p) && <span className="rowcap">{partLabel(p)}</span>}
                <div className="cell src" data-side="English">
                  <div className="tbox ro"><Painted text={p.source_text} /></div>
                </div>
                <div className="cell tgt" data-side={`${langName} — translating`}>
                  {vibeOnly ? (
                    <div className="tbox ro tgt-ro">
                      {p.value ? <Painted text={p.value} /> : <em className="faint">Not translated yet</em>}
                    </div>
                  ) : (
                  <div
                    ref={(el) => { boxes.current.set(p.unit_id, el); }}
                    className="tbox editable"
                    contentEditable
                    role="textbox"
                    aria-label={`${langName} ${partLabel(p) || 'text'}`}
                    suppressContentEditableWarning
                    spellCheck={false}
                    data-placeholder="Empty…"
                    onKeyDown={(e) => onFieldKey(e, i)}
                    onCompositionStart={() => { composing.current = true; }}
                    onCompositionEnd={(e) => { composing.current = false; onEdit(p, e.currentTarget); }}
                    onInput={(e) => onEdit(p, e.currentTarget)}
                  />
                  )}
                  {!vibeOnly && missing.length > 0 && (
                    <span className="missline">
                      {missing.map((t) => <span key={t} className="tok miss">{t} missing</span>)}
                    </span>
                  )}
                </div>
              </Fragment>
            );
          })}
        </div>

        {isDeck && askVibe && !asking && (
          <VibeSlider
            value={vibe}
            votes={card.vibe?.votes ?? 0}
            average={card.vibe?.average ?? 0}
            needed={needsVibe}
            sliderRef={slider}
            onChange={(n) => { setVibe(n); setVibeError(null); }}
            onPick={vibeOnly ? (n) => { setVibe(n); setVibeError(null); void rateAndGo(n); } : undefined}
            onCommit={() => void save()}
          />
        )}

        {vibeError && (
          <p className="hint" style={{ color: 'var(--crit)' }}>
            That vibe did not save: {vibeError}
          </p>
        )}

        {vibeOnly && (
          <div className="acts">
            <button className="btn sm pri" disabled={vibe === null} onClick={() => void save()}>
              Rate and next
            </button>
            {notesToggle}
            <span className="spacer" />
            <span className="editcue">
              Press a number from 0 to 9 and the next card comes up. The wording is not yours to
              judge here — only whether the card is any good.
            </span>
          </div>
        )}

        {!vibeOnly && !asking && (
          <div className="acts">
            <button
              className="btn sm pri"
              disabled={blocked || needsVibe}
              title={blocked
                ? 'Someone else already has a change in flight here'
                : needsVibe ? 'Give the card a vibe first' : undefined}
              onClick={() => void save()}
            >
              Save and confirm
            </button>
            <button className="btn sm" onClick={() => setAsking(true)}>Ask Sam</button>
            {notesToggle}
            {dirty && <button className="btn sm" onClick={fill}>Discard my edits</button>}
            <span className="spacer" />
            <span className="editcue">
              {needsVibe
                ? 'Rate the card before saving — it is the only thing we ask about the prompt itself.'
                : dirty
                  ? 'Your changes are saved to your batch — nothing is sent until you submit.'
                  : 'Correct anything that reads wrong, then save. Unchanged text is recorded as checked.'}
            </span>
          </div>
        )}

        {!vibeOnly && asking && (
          <div className="askbox">
            <textarea
              className="note" autoFocus value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="What's unclear? Sam sees this with the prompt attached."
            />
            <div className="acts">
              <button className="btn sm pri" disabled={!note.trim()}
                onClick={() => { onDecide(card, 'question', { note: note.trim() }); setAsking(false); }}>
                Add question to my batch
              </button>
              <button className="btn sm" onClick={() => { setAsking(false); setNote(''); }}>Cancel</button>
            </div>
          </div>
        )}

        {notesBlock}
      </div>
    </article>
  );
}
